import { Ban, Check, CheckCircle2, Clock, MapPin, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGiveLocationConsent, useMyProfile } from '@/features/profile';
import { errorMessageKey } from '@/shared/lib/errors';
import { Button } from '@/shared/ui/Button';

import { useRecordAttendance } from '../hooks/use-coverage';
import type { CoverageApplication } from '../model/coverage-application';

/**
 * Provider-facing GPS check-in / check-out, gated behind location consent.
 *
 * Product rules (handoff §04): consent is a distinct, refusable PDPL step; the
 * SERVER decides verified vs supplementary (the client only sends GPS); and an
 * unverified check-in is supporting evidence, NEVER an automatic no-show — so
 * that state is neutral/gold, never red, and says the manager decides.
 */
export function CheckInPanel({ application }: { application: CoverageApplication }): JSX.Element {
  const { t } = useTranslation();
  const profile = useMyProfile();
  const consent = useGiveLocationConsent();
  const record = useRecordAttendance(application.requestId);
  const [geoError, setGeoError] = useState<string | null>(null);

  const consented = profile.data?.locationConsentAt != null;

  function doRecord(kind: 'in' | 'out'): void {
    setGeoError(null);
    if (!navigator.geolocation) {
      setGeoError(t('checkin.geoError'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        record.mutate({
          applicationId: application.id,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          kind,
        }),
      () => setGeoError(t('checkin.geoError')),
    );
  }

  // Consent gate — a distinct, refusable PDPL step, not a buried toggle.
  if (!consented) {
    return (
      <section className="card stack">
        <div className="checkin-consent">
          <ShieldCheck className="checkin-consent__icon" aria-hidden />
          <div className="stack">
            <h4>{t('checkin.consentTitle')}</h4>
            <ul className="checkin-bullets">
              <li>{t('checkin.pdpl1')}</li>
              <li>{t('checkin.pdpl2')}</li>
              <li>{t('checkin.pdpl3')}</li>
            </ul>
          </div>
        </div>
        <Button loading={consent.isPending} onClick={() => consent.mutate()}>
          {t('checkin.giveConsent')}
        </Button>
      </section>
    );
  }

  const distanceM = record.data?.distanceM ?? null;

  return (
    <section className="card stack">
      <h4>{t('checkin.title')}</h4>

      {application.checkedInAt ? (
        <CheckinVerdict verified={application.checkinVerified === true} distanceM={distanceM} />
      ) : (
        <div className="checkin-target">
          <span className="checkin-target__ring" aria-hidden />
          <Button loading={record.isPending} onClick={() => doRecord('in')}>
            <MapPin size={18} aria-hidden />
            {t('checkin.recordNow')}
          </Button>
        </div>
      )}

      {application.checkedInAt && !application.checkedOutAt ? (
        <Button variant="outline" loading={record.isPending} onClick={() => doRecord('out')}>
          {t('checkin.checkOut')}
        </Button>
      ) : null}

      {application.checkedOutAt ? (
        <CheckinVerdict verified={application.checkoutVerified === true} distanceM={distanceM} />
      ) : null}

      {geoError ? (
        <div className="checkin-hero checkin-hero--denied" role="alert">
          <Ban className="checkin-hero__icon" aria-hidden />
          <strong>{t('checkin.deniedTitle')}</strong>
          <p className="muted">{geoError}</p>
        </div>
      ) : null}
      {record.isError ? (
        <p className="text-danger" role="alert">
          {t(errorMessageKey(record.error))}
        </p>
      ) : null}
    </section>
  );
}

/** The server's verdict. Verified → green success hero + in-range evidence.
 *  Unverified → gold "supplementary" hero that explicitly is NOT a failure and
 *  defers to the manager (never red). */
function CheckinVerdict({
  verified,
  distanceM,
}: {
  verified: boolean;
  distanceM: number | null;
}): JSX.Element {
  const { t } = useTranslation();

  if (verified) {
    return (
      <div className="checkin-hero checkin-hero--verified" role="status">
        <CheckCircle2 className="checkin-hero__icon" aria-hidden />
        <strong>{t('checkin.verifiedTitle')}</strong>
        <span className="badge" style={{ background: 'var(--color-surface-2)' }}>
          <span className="badge__icon" aria-hidden>
            <Check />
          </span>
          {distanceM != null
            ? t('checkin.distanceAway', { m: Math.round(distanceM) })
            : t('checkin.inRange')}
        </span>
      </div>
    );
  }

  return (
    <div className="checkin-hero checkin-hero--supplementary" role="status">
      <Clock className="checkin-hero__icon" aria-hidden />
      <strong>{t('checkin.unverifiedTitle')}</strong>
      <p className="muted">{t('checkin.supplementaryNote')}</p>
      {distanceM != null ? (
        <span className="muted">{t('checkin.distanceAway', { m: Math.round(distanceM) })}</span>
      ) : null}
    </div>
  );
}
